import React, { createContext, useContext, useEffect, useCallback } from 'react';

export type FontSize = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

export const FONT_SIZE_KEY = 'font-size';
export const DEFAULT_FONT_SIZE: FontSize = 2;

interface FontSizeContextType {
  fontSize: FontSize;
  setFontSize: (size: FontSize) => void;
}

const FontSizeContext = createContext<FontSizeContextType>({
  fontSize: DEFAULT_FONT_SIZE,
  setFontSize: () => {},
});

export const useFontSize = () => useContext(FontSizeContext);

const isValidFontSize = (size: number): size is FontSize => {
  return size >= 1 && size <= 8;
};

export const FontSizeProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [fontSize, setFontSize] = React.useState<FontSize>(() => {
    const storedSize = localStorage.getItem(FONT_SIZE_KEY);
    const parsedSize = storedSize ? parseInt(storedSize, 10) : DEFAULT_FONT_SIZE;
    return isValidFontSize(parsedSize) ? parsedSize : DEFAULT_FONT_SIZE;
  });

  const updateFontSize = useCallback((size: FontSize) => {
    if (isValidFontSize(size)) {
      setFontSize(size);
      localStorage.setItem(FONT_SIZE_KEY, size.toString());
    }
  }, []);

  useEffect(() => {
    // 移除所有字体大小类
    for (let i = 1; i <= 8; i++) {
      document.body.classList.remove(`font-size-${i}`);
    }
    // 添加当前字体大小类
    document.body.classList.add(`font-size-${fontSize}`);
  }, [fontSize]);

  return (
    <FontSizeContext.Provider value={{ fontSize, setFontSize: updateFontSize }}>
      {children}
    </FontSizeContext.Provider>
  );
};
